import { defineStore } from "pinia";
import { ref, computed, readonly } from "vue";
import { idGenerator } from "Util/uniqueIdGenerator";
import { SLOT_TYPES, NEW_GROUP_ID_BASE, NEW_MEMBER_ID_BASE } from "Types/channelTypes.js";
import { DATA_FIELD_TYPE } from "Types/dataFieldTypes.js";

const FIELD_MARKER = "f";
const GROUP_MARKER = "g";

// mirrors FieldAndGroupIdHelper.GetExternalTypeId
function _getExternalTypeId(type, isField) {
    return isField ? `${FIELD_MARKER}${type}` : `${GROUP_MARKER}${type}`;
}

const _channelIdGenerator = new idGenerator();

function _createGroupFromSchema(groupSchema) {
    const group = {
        Name: "",
        PublicId: _channelIdGenerator.getNextId(NEW_GROUP_ID_BASE),
        SchemaId: groupSchema.SchemaId,
        Slots: []
    };
    groupSchema.Slots.forEach((s) => {
        group.Slots.push(_createSlotFromSchema(s));
    });

    return group;
}

function _createSlotFromSchema(slotSchema) {
    const slotInstance = {
        SlotId: slotSchema.SlotId,
        Members: [_createMemberFromSchema(slotSchema)]
    };
    return slotInstance;
}

function _createMemberFromSchema(slotSchema) {
    const member = {
        ConnectionMemberId: _channelIdGenerator.getNextId(NEW_MEMBER_ID_BASE),
        DataType: null,
        MappedTo: "",
        Name: ""
    };

    // Set a default data type for data slots
    if (slotSchema.SlotType == SLOT_TYPES.Data) {
        if (slotSchema.FilterDataTypes) {
            member.DataType = slotSchema.FilterDataTypes[0];
        } else {
            member.DataType = "f1";
        }
    }

    // Set the name for non-customizable data slots
    if (!slotSchema.IsMultiInstance && !slotSchema.IsSignal) {
        member.Name = slotSchema.Name;
    }

    // Set the default member name if it exists
    if (slotSchema.DefaultMemberName) {
        member.Name = slotSchema.DefaultMemberName;
    }

    return member;
}

export const useChannelStore = defineStore("channelStore", () => {
    const channelName = ref("");
    const channelType = ref(null);
    const groupSchemas = ref(null);
    const groupInstances = ref(null);
    const sharedDefinitionPublicId = ref(null);
    const isPublishing = ref(true);

    const isEditingSchema = ref(false);
    const showArchived = ref(false);

    const availableChannelTypes = ref(null);
    const channelInUse = ref(false);
    const isNew = ref(true);
    const programDataTypes = ref(null);
    const _dataFields = ref(null);
    const _attachments = ref(null);
    const _roles = ref(null);
    const _forms = ref(null);
    const _statuses = ref(null);

    const channelHelpText = computed(() => {
        if (_channelDefinition.value) {
            return _channelDefinition.value.HelpText;
        }
        return null;
    });

    const channelSvgName = computed(() => {
        if (_channelDefinition.value) {
            return _channelDefinition.value.SvgName;
        }
        return null;
    });

    const _channelDefinition = computed(() => {
        if (channelType.value && availableChannelTypes.value) {
            return availableChannelTypes.value.find((c) => c.PublicId == channelType.value);
        }
        return null;
    });

    function loadState(model) {
        channelName.value = model.ChannelName;
        channelType.value = model.ChannelType;
        groupSchemas.value = model.Config.GroupSchemas ?? [];
        groupInstances.value = model.Config.GroupInstances ?? [];
        sharedDefinitionPublicId.value = model.Config.SharedDefinitionPublicId;

        channelInUse.value = model.IsInUse;
        isNew.value = groupInstances.value.length === 0 ? true : false;

        availableChannelTypes.value = model.AvailableChannelTypes;
        programDataTypes.value = model.ProgramDataTypes;
        _dataFields.value = model.DataDefinitions;
        _attachments.value = model.AttachmentDefinitions;
        _roles.value = model.Roles;
        _forms.value = model.Forms;
        _statuses.value = model.Statuses;
    }

    function setChannelName(value) {
        channelName.value = value;
    }

    function setChannelType(value) {
        channelType.value = value;
        sharedDefinitionPublicId.value = null;
        groupSchemas.value = [];
        groupInstances.value = [];
        if (_channelDefinition.value) {
            groupSchemas.value = _channelDefinition.value.GroupSchemas;
        }
        _initializeGroupInstances();
    }

    function setIsPublishing(value) {
        isPublishing.value = value;
    }

    function _initializeGroupInstances() {
        for (const groupSchema of groupSchemas.value) {
            if (groupSchema.IsMultiInstance) {
                continue;
            }
            const groups = groupInstances.value.filter((g) => g.SchemaId === groupSchema.SchemaId);
            if (groups.length === 0) {
                groupInstances.value.push(_createGroupFromSchema(groupSchema));
            }
            for (const group of groups) {
                for (const slot of group.Slots) {
                    const slotSchema = groupSchema.Slots.find((s) => s.SlotId === slot.SlotId);
                    if (!slotSchema.IsMultiInstance && slot.Members.length === 0) {
                        slot.Members.push(_createMemberFromSchema(slotSchema));
                    }
                }
            }
        }
    }

    function setIsEditingSchema(value) {
        isEditingSchema.value = value;
        if (value) {
            showArchived.value = true;
            _initializeGroupInstances();
        }
    }

    function setShowArchived(value) {
        showArchived.value = value;
    }

    function getGroupsBySchemaId(schemaId) {
        if (groupInstances.value) {
            return groupInstances.value.filter((gi) => gi.SchemaId == schemaId);
        }
        return [];
    }

    function getMappingOptions(slotType, dataType, allowReadOnly) {
        switch (slotType) {
            case SLOT_TYPES.Data:
                // DataFields need to be filtered on data type and readonly
                return _dataFields.value.filter((df) => {
                    if (df.IsReadOnly && !allowReadOnly) {
                        return false;
                    }
                    // Allow choice fields to be selected as strings if readonly types are allowed
                    if (
                        allowReadOnly &&
                        dataType === _getExternalTypeId(DATA_FIELD_TYPE.String, true) &&
                        df.TypeId === _getExternalTypeId(DATA_FIELD_TYPE.Choice, true)
                    ) {
                        return true;
                    }
                    return df.TypeId === dataType;
                });
            case SLOT_TYPES.Attachment:
                return _attachments.value;
            case SLOT_TYPES.Role:
                return _roles.value;
            case SLOT_TYPES.Form:
                return _forms.value;
            case SLOT_TYPES.Status:
                return _statuses.value;
        }
        return [];
    }

    function addGroup(schema) {
        groupInstances.value.push(_createGroupFromSchema(schema));
    }

    function removeGroup(groupId) {
        for (let i = 0; i < groupInstances.value.length; i++) {
            if (groupInstances.value[i].PublicId == groupId) {
                groupInstances.value.splice(i, 1);
                return;
            }
        }
    }

    function setGroupArchived(groupId, isArchived) {
        for (const group of groupInstances.value) {
            if (group.PublicId === groupId) {
                group.IsArchived = isArchived;
                return;
            }
        }
    }

    function addMember(groupId, slotId) {
        let targetSlotSchema;
        for (const groupSchema of groupSchemas.value) {
            for (const slotSchema of groupSchema.Slots) {
                if (slotSchema.SlotId === slotId) {
                    targetSlotSchema = slotSchema;
                }
            }
        }

        for (const group of groupInstances.value) {
            if (group.PublicId === groupId) {
                for (const slot of group.Slots) {
                    if (slot.SlotId === slotId) {
                        const newMember = _createMemberFromSchema(targetSlotSchema);
                        slot.Members.push(newMember);
                        if (targetSlotSchema.DefaultMemberName && !hasValidMemberName(newMember)) {
                            let nextId = 2;
                            while (!hasValidMemberName(newMember)) {
                                newMember.Name = targetSlotSchema.DefaultMemberName + " " + nextId;
                                nextId++;
                            }
                        }
                    }
                }
            }
        }
    }

    function removeMember(memberId) {
        for (const group of groupInstances.value) {
            for (const slot of group.Slots) {
                for (let i = 0; i < slot.Members.length; i++) {
                    if (slot.Members[i].ConnectionMemberId == memberId) {
                        slot.Members.splice(i, 1);
                        return;
                    }
                }
            }
        }
    }

    function _findMember(memberId) {
        for (const group of groupInstances.value) {
            for (const slot of group.Slots) {
                for (const member of slot.Members) {
                    if (member.ConnectionMemberId === memberId) {
                        return member;
                    }
                }
            }
        }
    }

    function setMemberArchived(memberId, isArchived) {
        const member = _findMember(memberId);
        member.IsArchived = isArchived;
    }

    function setMemberType(memberId, dataType) {
        const member = _findMember(memberId);
        member.DataType = dataType;
        member.MappedTo = "";
    }

    function setMemberName(memberId, name) {
        const member = _findMember(memberId);
        member.Name = name;
    }

    function setMemberMappedTo(memberId, mappedTo) {
        const member = _findMember(memberId);
        member.MappedTo = mappedTo;
    }

    function hasValidMemberName(targetMember) {
        // find the slot the member is in
        let targetSlot;
        for (const group of groupInstances.value) {
            for (const slot of group.Slots) {
                for (const member of slot.Members) {
                    if (member.ConnectionMemberId === targetMember.ConnectionMemberId) {
                        targetSlot = slot;
                        break;
                    }
                }
            }
        }

        // check if the member name is duplicated in the same slot
        const targetMemberName = targetMember.Name.trim().toLowerCase();
        for (const member of targetSlot.Members) {
            if (member.ConnectionMemberId === targetMember.ConnectionMemberId) {
                continue;
            }
            if (member.Name.trim().toLowerCase() === targetMemberName) {
                return false;
            }
        }
        return true;
    }

    function initializeChannelFromImport(model) {
        channelType.value = model.ClassName;
        groupInstances.value = model.GroupInstances;
        groupSchemas.value = model.GroupSchemas;
        sharedDefinitionPublicId.value = model.SharedDefinitionPublicId;

        // if there is no shared def then this channel is editable and needs to initialize
        if (!sharedDefinitionPublicId.value) {
            _initializeGroupInstances();
        }
    }

    return {
        // channel properties
        channelName: readonly(channelName),
        setChannelName,
        channelType: readonly(channelType),
        setChannelType,
        isPublishing: readonly(isPublishing),
        setIsPublishing,
        channelHelpText,
        channelSvgName,
        groupSchemas: readonly(groupSchemas),
        groupInstances: readonly(groupInstances),
        sharedDefinitionPublicId: readonly(sharedDefinitionPublicId),

        // initialization
        loadState,
        initializeChannelFromImport,

        // edit page properties
        channelInUse: readonly(channelInUse),
        isNew: readonly(isNew),
        isEditingSchema: readonly(isEditingSchema),
        setIsEditingSchema,
        showArchived: readonly(showArchived),
        setShowArchived,

        // program data
        availableChannelTypes: readonly(availableChannelTypes),
        programDataTypes: readonly(programDataTypes),

        // group manipulation
        getGroupsBySchemaId,
        addGroup,
        removeGroup,
        setGroupArchived,

        // member manipulation
        getMappingOptions,
        addMember,
        removeMember,
        setMemberArchived,
        setMemberType,
        setMemberName,
        setMemberMappedTo,
        hasValidMemberName
    };
});
